import { cn, getLuxonLocale } from '@/lib/utils';
import {
  Plus,
  ArrowRight,
  ArrowLeft,
  CalendarX2,
  Copy,
  Eye,
} from 'lucide-react';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { WithTooltip } from './with-tooltip';
import { Link, useParams } from 'react-router';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { useI18nContext } from '@/i18n/i18n-react';
import { CancelCampaignDialog } from '@/components/campaigns/cancel-campaign-dialog';
import type { CampaignDto } from '@/generated/korza-api-client/types.gen';
import { useCancelCampaign } from '@/hooks/use-cancel-campaign';
import { canCancelCampaign } from '@/lib/campaign-list';

interface Event {
  id: string;
  title: string;
  start: DateTime;
  status: CampaignDto['status'];
}

interface CalendarProps {
  currentDate: DateTime;
  setCurrentDate: (date: DateTime) => void;
  events: Event[];
}

/**
 * Month grid of campaigns (desktop). Clicking an event opens its actions
 * (Ver detalhes, Duplicar, Cancelar while not sent), with no right click;
 * each day's "+" is a link to the composer that also shows on keyboard focus
 * and on touch screens (PUSH-04 AC 5).
 */
export function Calendar({ currentDate, events }: CalendarProps) {
  const { LL } = useI18nContext();
  const cancelCampaign = useCancelCampaign();
  const [cancelTarget, setCancelTarget] = useState<Event | null>(null);
  const daysOfWeek = [
    LL.calendar.daysOfWeek.sunday(),
    LL.calendar.daysOfWeek.monday(),
    LL.calendar.daysOfWeek.tuesday(),
    LL.calendar.daysOfWeek.wednesday(),
    LL.calendar.daysOfWeek.thursday(),
    LL.calendar.daysOfWeek.friday(),
    LL.calendar.daysOfWeek.saturday(),
  ];

  const firstDayOfMonth = useMemo(
    () => currentDate.startOf('month').weekday,
    [currentDate]
  );

  const daysInLastMonth = useMemo(() => {
    const days =
      currentDate.startOf('month').minus({ days: firstDayOfMonth })
        .daysInMonth || 1;
    return [...Array(days).keys()].splice(days - firstDayOfMonth);
  }, [currentDate, firstDayOfMonth]);

  const daysInMonth = useMemo(
    () => [...Array(currentDate.daysInMonth).keys()],
    [currentDate]
  );

  const daysInNextMonth = useMemo(() => {
    const totalDays = daysInLastMonth.length + daysInMonth.length;
    const remainingDays = totalDays % 7 === 0 ? 0 : 7 - (totalDays % 7);
    return [...Array(remainingDays).keys()];
  }, [daysInLastMonth, daysInMonth]);

  return (
    <div className="w-full h-full">
      <div className="w-full flex justify-between bg-border gap-[0.5px] border-t border-x rounded-t-lg overflow-hidden border-border">
        {daysOfWeek.map((day) => (
          <span
            key={day}
            className="flex text-center items-center bg-background justify-center w-full text-stone-500 font-semibold text-sm"
          >
            {day}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-7 w-full bg-border gap-[0.5px] border border-border rounded-b-lg overflow-hidden h-full">
        {daysInLastMonth.map((day) => (
          <CalendarDay
            key={`last-month-${day}`}
            day={day}
            currentDate={currentDate.minus({ month: 1 })}
            style="opaque"
            events={events}
            onCancel={setCancelTarget}
          />
        ))}
        {daysInMonth.map((day) => (
          <CalendarDay
            key={`current-month-${day}`}
            day={day}
            currentDate={currentDate}
            style={
              DateTime.now().day ===
                currentDate.startOf('month').plus({ day }).day &&
              DateTime.now().month === currentDate.month &&
              DateTime.now().year === currentDate.year
                ? 'highlighted'
                : 'default'
            }
            events={events}
            onCancel={setCancelTarget}
          />
        ))}
        {daysInNextMonth.map((day) => (
          <CalendarDay
            key={`next-month-${day}`}
            day={day}
            currentDate={currentDate.plus({ month: 1 })}
            style="opaque"
            events={events}
            onCancel={setCancelTarget}
          />
        ))}
      </div>
      <CancelCampaignDialog
        open={cancelTarget !== null}
        onOpenChange={(open) => {
          if (!open && !cancelCampaign.isPending) setCancelTarget(null);
        }}
        campaignName={cancelTarget?.title ?? ''}
        isPending={cancelCampaign.isPending}
        onConfirm={() => {
          if (!cancelTarget) return;
          cancelCampaign.mutate(
            { campaignId: cancelTarget.id, campaignName: cancelTarget.title },
            { onSettled: () => setCancelTarget(null) }
          );
        }}
      />
    </div>
  );
}

interface CalendarDayProps {
  currentDate: DateTime;
  day: number;
  style?: 'default' | 'opaque' | 'highlighted';
  events?: Event[];
  onCancel(event: Event): void;
}

function CalendarDay({
  day,
  currentDate,
  style = 'default',
  events,
  onCancel,
}: CalendarDayProps) {
  const { LL, locale } = useI18nContext();
  const tenantSlug = useParams().tenantSlug ?? '';
  const date = currentDate.startOf('month').plus({ day });

  return (
    <span
      key={day + 1}
      className={cn(
        'w-full px-1 min-h-24 text-sm group bg-background overflow-scroll no-scrollbar',
        {
          'text-stone-400': style === 'opaque',
          'text-black': style === 'highlighted',
        }
      )}
    >
      <span className="flex pt-1 flex-row justify-between items-start mb-1 sticky top-0 bg-gradient-to-b from-background to-transparent via-background/90">
        <span
          className={cn(
            'rounded-full flex items-center justify-center w-6 h-6 px-2',
            {
              'bg-primary text-primary-foreground': style === 'highlighted',
              'w-auto': day === 0,
            }
          )}
        >
          {day === 0
            ? `${currentDate.toFormat('LLL', { locale: locale })} ${day + 1}`
            : day + 1}
        </span>
        <Link
          to={`/${tenantSlug}/campaigns/create?date=${encodeURI(date.toISO() || '')}`}
          aria-label={LL.calendar.newCampaignOn({
            date: date
              .setLocale(getLuxonLocale(locale))
              .toLocaleString(DateTime.DATE_SHORT),
          })}
          className="p-1.5 bg-accent/60 rounded-full border border-accent hover:bg-accent/40 transition-colors opacity-0 group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Plus height={16} width={16} color="gray" aria-hidden />
        </Link>
      </span>

      <span className="flex flex-col gap-1 mb-1">
        {events
          ?.filter(
            (event) =>
              event.start.day === day + 1 &&
              event.start.month === currentDate.month &&
              event.start.year === currentDate.year
          )
          .map((event) => (
            <CalendarEvent key={event.id} event={event} onCancel={onCancel} />
          ))}
      </span>
    </span>
  );
}

interface CalendarEventProps {
  event: Event;
  onCancel(event: Event): void;
}

const eventActionClassName =
  'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent focus-visible:bg-accent';

function CalendarEvent({ event, onCancel }: CalendarEventProps) {
  const { LL } = useI18nContext();
  const tenantSlug = useParams().tenantSlug ?? '';
  const [open, setOpen] = useState(false);
  const upcoming = event.start >= DateTime.now();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex w-full min-w-0 flex-row justify-between items-center bg-accent/80 rounded-sm p-1 font-medium text-xs text-gray-300 gap-1 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            {
              'bg-primary text-primary-foreground hover:bg-primary/90':
                upcoming,
            }
          )}
        >
          <span
            className={cn('text-gray-400', {
              'text-primary-foreground': upcoming,
            })}
          >
            {event.start.toFormat('HH:mm')}
          </span>
          <span className="whitespace-nowrap overflow-hidden text-ellipsis">
            {event.title}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-1">
        <p className="truncate px-2 py-1.5 text-sm font-semibold">
          {event.title}
        </p>
        <Link
          to={`/${tenantSlug}/campaigns/${event.id}`}
          className={eventActionClassName}
        >
          <Eye className="h-4 w-4" aria-hidden />
          {LL.campaigns.actionsSheet.viewDetails()}
        </Link>
        <Link
          to={`/${tenantSlug}/campaigns/create?from=${event.id}`}
          className={eventActionClassName}
        >
          <Copy className="h-4 w-4" aria-hidden />
          {LL.campaigns.actionsSheet.duplicate()}
        </Link>
        {canCancelCampaign(event) && (
          <button
            type="button"
            className={cn(eventActionClassName, 'text-destructive')}
            onClick={() => {
              setOpen(false);
              onCancel(event);
            }}
          >
            <CalendarX2 className="h-4 w-4" aria-hidden />
            {LL.campaigns.actionsSheet.cancel()}
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface CalendarNavigationProps {
  currentDate: DateTime;
  setCurrentDate: (date: DateTime) => void;
}

export function CalendarNavigation({
  currentDate,
  setCurrentDate,
}: CalendarNavigationProps) {
  const { LL } = useI18nContext();

  return (
    <div className="flex flex-row items-center">
      <WithTooltip tooltip={LL.calendar.previousMonth()}>
        <Button
          size={'sm'}
          variant="ghost"
          onClick={() => setCurrentDate(currentDate.minus({ month: 1 }))}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
      </WithTooltip>
      <Button
        size={'sm'}
        variant="ghost"
        onClick={() => setCurrentDate(DateTime.now())}
      >
        {LL.calendar.today()}
      </Button>
      <WithTooltip tooltip={LL.calendar.nextMonth()}>
        <Button
          size={'sm'}
          variant="ghost"
          onClick={() => setCurrentDate(currentDate.plus({ month: 1 }))}
        >
          <ArrowRight className="h-4 w-4" />
        </Button>
      </WithTooltip>
    </div>
  );
}
